import React, { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { GENDERS } from "@/config/leads";

const EMPTY = {
  full_name: "", phone: "", whatsapp: "", email: "", dob: "", gender: "",
  address: "", city: "", preferred_language: "", insurance_provider: "",
  insurance_id: "", notes: "", owner_id: "",
};

const Field = ({ label, required, children, className = "" }) => (
  <div className={`space-y-1.5 ${className}`}>
    <Label className="text-slate-700">{label}{required && <span className="text-red-500"> *</span>}</Label>
    {children}
  </div>
);

export function PatientFormDialog({ open, onOpenChange, patient, owners = [], onSaved }) {
  const isEdit = Boolean(patient);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (open) {
      setErrors({});
      if (patient) setForm({ ...EMPTY, ...Object.fromEntries(Object.keys(EMPTY).map((k) => [k, patient[k] ?? ""])) });
      else setForm(EMPTY);
    }
  }, [open, patient]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const setInput = (k) => (e) => set(k)(e.target.value);

  const submit = async () => {
    const e = {};
    if (!form.full_name.trim()) e.full_name = "Required";
    if (!form.phone.trim()) e.phone = "Required";
    setErrors(e);
    if (Object.keys(e).length) return;

    setSaving(true);
    const payload = {};
    Object.entries(form).forEach(([k, v]) => {
      if (v === "" || v === null) { if (isEdit) payload[k] = null; return; }
      payload[k] = v;
    });
    try {
      if (isEdit) await api.put(`/patients/${patient.id}`, payload);
      else await api.post("/patients", payload);
      toast.success(isEdit ? "Patient updated" : "Patient created");
      onOpenChange(false);
      onSaved && onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl" data-testid="patient-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{isEdit ? "Edit Patient" : "Add Patient"}</DialogTitle>
          <DialogDescription>Maintain the CRM patient record.</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 gap-3 py-2 sm:grid-cols-2">
          <Field label="Full name" required>
            <Input value={form.full_name} onChange={setInput("full_name")} data-testid="patient-input-full_name" />
            {errors.full_name && <p className="text-xs text-red-600">{errors.full_name}</p>}
          </Field>
          <Field label="Phone" required>
            <Input value={form.phone} onChange={setInput("phone")} data-testid="patient-input-phone" />
            {errors.phone && <p className="text-xs text-red-600">{errors.phone}</p>}
          </Field>
          <Field label="WhatsApp"><Input value={form.whatsapp} onChange={setInput("whatsapp")} /></Field>
          <Field label="Email"><Input value={form.email} onChange={setInput("email")} /></Field>
          <Field label="Date of birth"><Input type="date" value={form.dob} onChange={setInput("dob")} /></Field>
          <Field label="Gender">
            <Select value={form.gender || undefined} onValueChange={set("gender")}>
              <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{GENDERS.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Address" className="sm:col-span-2"><Input value={form.address} onChange={setInput("address")} /></Field>
          <Field label="City"><Input value={form.city} onChange={setInput("city")} /></Field>
          <Field label="Preferred language"><Input value={form.preferred_language} onChange={setInput("preferred_language")} /></Field>
          <Field label="Insurance provider"><Input value={form.insurance_provider} onChange={setInput("insurance_provider")} /></Field>
          <Field label="Insurance / member ID"><Input value={form.insurance_id} onChange={setInput("insurance_id")} /></Field>
          <Field label="Assigned owner" className="sm:col-span-2">
            <Select value={form.owner_id || undefined} onValueChange={set("owner_id")}>
              <SelectTrigger data-testid="patient-select-owner"><SelectValue placeholder="Unassigned" /></SelectTrigger>
              <SelectContent>{owners.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <div className="space-y-1.5 sm:col-span-2">
            <Label>Notes</Label>
            <Textarea rows={3} value={form.notes} onChange={setInput("notes")} data-testid="patient-input-notes" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="patient-form-submit">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}{isEdit ? "Save" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
